const abilityKeys = [
  'viewApiKeys',
  'manageApiKeys',
  'viewDashboard',
  'viewDonations',
  'viewFundraisers',
  'manageFundraisers',
  'viewOpportunities',
  'manageOpportunities',
  'manageOpportunitySurveys',
  'manageOpportunityWaivers',
  'viewSurveyTemplates',
  'manageSurveyTemplates',
  'viewWaiverTemplates',
  'manageWaiverTemplates',
  'viewGivingCampaigns',
  'manageGivingCampaigns',
  'viewGivingActions',
  'manageGivingActions',
  'viewGrants',
  'viewGrantPrograms',
  'manageGrantReviews',
  'manageGrantEvents',
  'assignGrantsTasks',
  'readGrantDocuments',
  'uploadGrantDocuments',
  'inviteGrantCandidates',
  'manageDonationWallet',
  'viewUsers',
  'manageUsers',
  'manageOwners',
  'manageAdmins',
  'manageDevelopers',
  'manageMembers',
  'manageGrantReviewers',
  'viewWorkplaceGiving',
  'viewCategories',
  'manageCategories',
  'viewNonprofits',
  'viewOrganisations',
  'viewVerifications',
  'viewPartnerInsights'
] as const
export type AbilityKey = typeof abilityKeys[number]
export type DefaultRoleId = 'pd:member' | 'pd:developer' | 'pd:admin' | 'pd:owner' | 'pd:grants_reviewer'

const permissions = {
  // IAM Domain
  api_keys: ['read', 'write'],
  pd_dashboard: ['access'],
  users: ['read', 'write'],
  user_invite: ['write'],
  user_role_owner: ['write'],
  user_role_admin: ['write'],
  user_role_developer: ['write'],
  user_role_member: ['write'],
  user_role_grants_reviewer: ['write'],
  user_role_wg: ['write'],

  // Donation Domain
  donations: ['read'],

  // Validation Domain
  validation_submissions: ['read'],
  validation_requests: ['read'],
  agent_verifications: ['read'],

  // Categories Domain
  categories: ['read'],

  // Disbursement Domain

  // Organization Domain
  organizations: ['read'],

  // Partner Fundraisers
  partner_fundraiser: ['read', 'write'],

  // Partner Opportunities
  opportunities: ['read', 'write'],
  opportunities_surveys: ['write'],
  opportunities_waivers: ['write'],
  opportunities_survey_templates: ['read', 'write'],
  opportunities_waiver_templates: ['read', 'write'],

  // Partner Giving Campaigns
  giving_campaigns: ['read', 'write'],
  giving_action: ['read', 'write'],

  // Grants
  grants: ['read'],
  grants_programs: ['read'],
  grants_candidate_review: ['write'],
  grants_admin_event: ['write'],
  grants_task_assignee: ['write'],
  grants_candidate_document: ['read', 'write'],
  grants_form_submissions: ['organisation:read'],
  grants_invite: ['write'],

  // Donation Wallet
  donation_wallet: ['write'],

  // Partner Insights
  partner_insights: ['read']
} as const

type PermissionString<T extends Record<string, readonly string[]>, K extends keyof T = keyof T> = K extends string
  ? `${K}:${T[K][number]}`
  : never
export type Permission = PermissionString<typeof permissions>

export const abilityDefinitions: Record<AbilityKey, Permission[]> = {
  viewApiKeys: ['api_keys:read'],
  manageApiKeys: ['api_keys:write'],
  manageOwners: ['user_role_owner:write'],
  manageAdmins: ['user_role_admin:write'],
  manageDevelopers: ['user_role_developer:write'],
  manageMembers: ['user_role_member:write'],
  manageGrantReviewers: ['user_role_grants_reviewer:write'],
  viewWorkplaceGiving: ['user_role_wg:write'],
  viewDashboard: ['pd_dashboard:access'],
  viewDonations: ['donations:read'],
  viewFundraisers: ['partner_fundraiser:read'],
  manageFundraisers: ['partner_fundraiser:write'],
  viewOpportunities: ['opportunities:read'],
  manageOpportunities: ['opportunities:write'],
  manageOpportunitySurveys: ['opportunities_surveys:write'],
  manageOpportunityWaivers: ['opportunities_waivers:write'],
  // Read is scoped to existing permissions: no `opportunities_*_templates:read` exists on the
  // backend (only the new `:write` perms). A user who can manage opportunity surveys/waivers, or
  // who can manage templates, can view templates.
  viewSurveyTemplates: ['opportunities_surveys:write', 'opportunities_survey_templates:write'],
  manageSurveyTemplates: ['opportunities_survey_templates:write'],
  viewWaiverTemplates: ['opportunities_waivers:write', 'opportunities_waiver_templates:write'],
  manageWaiverTemplates: ['opportunities_waiver_templates:write'],
  viewGivingCampaigns: ['giving_campaigns:read'],
  manageGivingCampaigns: ['giving_campaigns:write'],
  viewGivingActions: ['giving_action:read'],
  manageGivingActions: ['giving_action:write'],
  viewGrants: ['grants:read'],
  viewGrantPrograms: ['grants_programs:read'],
  manageGrantReviews: ['grants_candidate_review:write'],
  manageGrantEvents: ['grants_admin_event:write'],
  // Reviewers (Grants Reviewer / Lead Reviewer) reach documents via
  // grants_form_submissions:organisation:read; they see organisation-audience documents only and
  // cannot upload (uploadGrantDocuments still requires grants_candidate_document:write). The rule
  // list is OR-matched in usePermissionCheck, so full-access roles are unaffected.
  readGrantDocuments: ['grants_candidate_document:read', 'grants_form_submissions:organisation:read'],
  uploadGrantDocuments: ['grants_candidate_document:write'],
  assignGrantsTasks: ['grants_task_assignee:write'],
  inviteGrantCandidates: ['grants_invite:write'],
  manageDonationWallet: ['donation_wallet:write'],
  viewUsers: ['users:read'],
  manageUsers: ['users:write'],
  viewCategories: ['categories:read'],
  // Scoped to an existing permission: no `categories:write` exists on the backend yet, so managing
  // organisations in a category currently follows read access. Switch to `categories:write` once
  // the backend introduces it.
  manageCategories: ['categories:read'],
  viewNonprofits: ['organizations:read'],
  viewOrganisations: ['organizations:read'],
  viewVerifications: ['validation_submissions:read'],
  viewPartnerInsights: ['partner_insights:read']
}
